import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import {
  DCR_APPROVAL_STATUS,
  DCR_APPROVAL_STATUS_LABELS,
  type DcrApprovalStatus,
} from '../constants/moTravel.constants';
import type { MoTravelDay } from '../types/moTravel.types';

const DCR_APPROVAL_STATUS_CARD_CLASS: Record<DcrApprovalStatus, string> = {
  APPROVED: 'bg-palette-emerald/10 text-palette-emerald',
  RECOMMENDED: 'bg-palette-blue/10 text-palette-blue',
  PENDING: 'bg-palette-amber/10 text-palette-amber',
  REJECTED: 'bg-palette-rose/10 text-palette-rose',
};

const APPROVAL_DISPLAY_ORDER: DcrApprovalStatus[] = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'RECOMMENDED',
];

const WIDER_RECOMMENDED_COLUMNS_CLASS = '@[22rem]:grid-cols-[1fr_1fr_1fr_1.3fr]';

const EMPTY_STOP_COUNTS: Record<DcrApprovalStatus, number> = {
  APPROVED: 0,
  RECOMMENDED: 0,
  PENDING: 0,
  REJECTED: 0,
};

interface MoApprovalBarProps {
  days: MoTravelDay[];
  className?: string;
}

export function MoApprovalBar({ days, className }: MoApprovalBarProps) {
  const stopCountByStatus = useMemo(() => {
    const countByStatus = { ...EMPTY_STOP_COUNTS };
    for (const day of days) {
      for (const stop of day.stops) countByStatus[stop.approvalStatus] += 1;
    }
    return countByStatus;
  }, [days]);

  const totalStops = DCR_APPROVAL_STATUS.reduce(
    (sum, status) => sum + stopCountByStatus[status],
    0,
  );
  if (totalStops === 0) return null;

  return (
    <section
      aria-label="DCR approval"
      className={cn('@container flex flex-col gap-2.5', className)}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          DCR approval
        </h3>
        <span className="text-sm text-muted-foreground tabular-nums">
          Total <span className="font-semibold text-foreground">{formatNumber(totalStops)}</span>
        </span>
      </div>
      <ul className={cn('grid grid-cols-2 gap-1.5', WIDER_RECOMMENDED_COLUMNS_CLASS)}>
        {APPROVAL_DISPLAY_ORDER.map((status) => (
          <li
            key={status}
            className={cn(
              'flex min-w-0 flex-col gap-0.5 rounded-lg px-1.5 py-2',
              DCR_APPROVAL_STATUS_CARD_CLASS[status],
            )}
          >
            <span className="text-xl leading-tight font-semibold tabular-nums">
              {formatNumber(stopCountByStatus[status])}
            </span>
            <span className="text-xs font-medium whitespace-nowrap">
              {DCR_APPROVAL_STATUS_LABELS[status]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
